import { StrictMode } from "react";
import ReactDOM from "react-dom/client";
import App from "./App";
import { APIProvider } from "@vis.gl/react-google-maps";
import * as Sentry from "@sentry/react";
import { logger } from "./utilities/logger";

const isDev = import.meta.env.VITE_ENVIRONMENT_NAME === "development";
const isLocalhostInUrl = window.location.hostname.search("localhost") !== -1;
const enableSentry = import.meta.env.VITE_ENABLE_SENTRY === "true";

Sentry.init({
	dsn: import.meta.env.VITE_SENTRY_DSN,
	environment: import.meta.env.VITE_ENVIRONMENT_NAME,
	beforeSend(event, hint) {
		if (event.exception && event.event_id && !hint?.mechanism?.handled) {
			Sentry.showReportDialog({ eventId: event.event_id });
		}
		return event;
	},
	enableLogs: true,
	integrations: [
		Sentry.browserTracingIntegration(),
		Sentry.browserProfilingIntegration(),
		Sentry.replayIntegration(),
		Sentry.feedbackIntegration({
			colorScheme: "system",
			enableScreenshot: true,
			showBranding: false,
		}),
	],
	tracesSampleRate: isDev ? 1.0 : 1.0, // capture 100% of transactions
	// - this should probably be smaller in production
	// - but for now I want to see what happens
	tracePropagationTargets: [
		// "localhost",
		// /^https:\/\/.*\.awsapprunner\.com/,
		import.meta.env.VITE_ROGO_API_URL,
	],
	profilesSampleRate: 1.0, // profile 100% of captured transactions
	replaysSessionSampleRate: isDev ? 1.0 : 0.1, // capture replays on 10% of sessions
	replaysOnErrorSampleRate: 1.0, // capture replays on 100% of errors
	enabled: (!isDev && !isLocalhostInUrl) || enableSentry,
});

Sentry.setTag("hostname", window.location.hostname);
// @ts-ignore
logger.sentry = Sentry;
logger.newSession();

ReactDOM.createRoot(document.getElementById("root") as HTMLElement).render(
	<Sentry.ErrorBoundary
		fallback={
			<>
				<p>
					An unexpected error has occurred. The ROGO software team has been notified. Please reach out to
					software@rogoag.com or use our{" "}
					<a href="https://airtable.com/appG2uelyCZuENMT8/pagzG52leZoMbzYb8/form" target="_blank" rel="noreferrer">
						software feedback form
					</a>{" "}
					with any additional questions or concerns.
				</p>
				<p>
					Please try to refresh your browser and visit{" "}
					<a href="https://portal.rogoag.com/jobs/dashboard">your dashboard</a> again
				</p>
			</>
		}
	>
		<StrictMode>
			<APIProvider apiKey={import.meta.env.VITE_GOOGLE_MAPS_API_KEY}>
				<App />
			</APIProvider>
		</StrictMode>
	</Sentry.ErrorBoundary>,
);
